import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getLicenseTelegramUsername, normalizeTelegramUsername } from '../utils/licenseTelegram';
import {
  PLAN_ADDON_OPTIONS,
  PlanAddonKey,
  createEmptyAddonSelection,
  formatAddonEur,
  getSelectedAddonsCryptoTotal,
  getSelectedAddonKeys,
} from '../utils/addonPricing';
import './CrackerController.css';
import './ConfigManager.css';
import './MailerManager.css';
import './GrabberManager.css';
import './Auth.css';
import './Payment.css';

interface CryptoPrice {
  BTC: number;
  ETH: number;
  SOL: number;
}

interface PricingPlan {
  name: string;
  price: string;
  period: string;
  cryptoPrice: CryptoPrice;
  planId: string;
}

interface PaymentModalProps {
  plan: PricingPlan;
  onClose: () => void;
  onSuccess: () => void;
  /** Pré-remplit le champ Telegram (ex. nom d'utilisateur de la licence) */
  telegramUsername?: string;
}

interface PaymentData {
  paymentId: string;
  paymentAddress: string;
  paymentAmount: number;
  paymentCurrency: string;
  expiresAt: string;
  status: 'pending' | 'completed' | 'expired';
}

const PaymentModal: React.FC<PaymentModalProps> = ({ plan, onClose, onSuccess, telegramUsername: telegramUsernameProp }) => {
  const [selectedCrypto, setSelectedCrypto] = useState<'BTC' | 'ETH' | 'SOL'>('BTC');
  const [telegramUsername, setTelegramUsername] = useState('');
  const [selectedAddons, setSelectedAddons] = useState(createEmptyAddonSelection);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(1200);

  const lockedTelegram = Boolean(telegramUsernameProp?.trim() || getLicenseTelegramUsername());

  useEffect(() => {
    const fromProp = telegramUsernameProp?.trim();
    const fromLicense = getLicenseTelegramUsername();
    const value = fromProp || fromLicense;
    if (value) {
      setTelegramUsername(normalizeTelegramUsername(value));
    }
  }, [telegramUsernameProp]);

  const cryptoSymbols = { BTC: '₿', ETH: 'Ξ', SOL: '◎' };

  useEffect(() => {
    if (paymentData && paymentData.status === 'pending') {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [paymentData]);

  useEffect(() => {
    if (paymentData && paymentData.status === 'pending') {
      const checkPaymentStatus = async () => {
        try {
          const response = await fetch(`https://glitchcloud.app/api/payment/status/${paymentData.paymentId}`);
          const data = await response.json();

          if (data.status === 'completed') {
            setPaymentData({ ...paymentData, status: 'completed' });
            setTimeout(() => onSuccess(), 2000);
          }
        } catch (err) {
          console.error('Erreur vérification paiement:', err);
        }
      };

      const interval = setInterval(checkPaymentStatus, 10000);
      return () => clearInterval(interval);
    }
  }, [paymentData, onSuccess]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleAddon = (key: PlanAddonKey) => {
    setSelectedAddons((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const addonsCryptoTotal = getSelectedAddonsCryptoTotal(selectedAddons, selectedCrypto);

  const getTotalAmount = () => plan.cryptoPrice[selectedCrypto] + addonsCryptoTotal;

  const handlePayment = async () => {
    if (!telegramUsername.trim()) {
      setError('Enter your Telegram username');
      return;
    }

    if (!telegramUsername.startsWith('@')) {
      setTelegramUsername('@' + telegramUsername);
    }

    setIsLoading(true);
    setError('');

    try {
      const selectedKeys = getSelectedAddonKeys(selectedAddons);

      const requestBody = {
        planId: plan.planId,
        planName: plan.name,
        amount: plan.cryptoPrice[selectedCrypto] + addonsCryptoTotal,
        currency: selectedCrypto,
        telegramUsername: telegramUsername.startsWith('@') ? telegramUsername : '@' + telegramUsername,
        includeGrabber: selectedAddons.grabber,
        includeMailer: selectedAddons.mailer,
        includeDebouncer: selectedAddons.debouncer,
        includeStripe: selectedAddons.stripe,
        addons: selectedKeys,
      };

      console.log('📤 Données envoyées au serveur:', requestBody);

      const response = await fetch('https://glitchcloud.app/api/payment/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      console.log('📥 Réponse du serveur:', data);

      if (data.success) {
        setPaymentData(data.payment);
        setTimeLeft(900);
      } else {
        console.error('❌ Erreur serveur:', data);
        setError(data.message || 'Error creating payment');
      }
    } catch (err) {
      console.error('Erreur paiement:', err);
      setError('Could not reach the payment server');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (paymentData && paymentData.status === 'completed') {
    return (
      <div className="cracker-modal-overlay">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cracker-modal max-w-md"
        >
          <div className="cracker-modal-body text-center py-6">
            <div className="pay-success-icon">✓</div>
            <h2 className="cracker-heading mb-2">Payment successful</h2>
            <p className="text-sm text-[#737373] mb-6 leading-relaxed">
              Your {plan.name} license is active. Sign-in details will be sent via Telegram.
            </p>
            <button type="button" onClick={onSuccess} className="cracker-btn cracker-btn--gold w-full">
              Continue
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="cracker-modal-overlay" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        className="cracker-modal max-w-lg"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <div className="cracker-modal-header">
          <div>
            <p className="cracker-label mb-1">Crypto payment</p>
            <h2 className="cracker-heading">Plan {plan.name}</h2>
          </div>
          <button type="button" onClick={onClose} className="gm-modal-close" aria-label="Close">
            ×
          </button>
        </div>

        <div className="cracker-modal-body">
          {!paymentData ? (
            <div className="space-y-4">
              <div>
                <p className="cracker-section-title mb-2">Summary</p>
                <div className="pay-summary">
                  <div className="pay-summary-row">
                    <span className="pay-summary-label">Plan</span>
                    <span className="pay-summary-value">{plan.name}</span>
                  </div>
                  <div className="pay-summary-row">
                    <span className="pay-summary-label">Duration</span>
                    <span className="pay-summary-value">{plan.period}</span>
                  </div>
                  <div className="pay-summary-row">
                    <span className="pay-summary-label">Prix</span>
                    <span className="pay-summary-value">{plan.price}</span>
                  </div>
                  {PLAN_ADDON_OPTIONS.map((addon) =>
                    selectedAddons[addon.key] ? (
                      <div key={addon.key} className="pay-summary-row">
                        <span className="pay-summary-label">{addon.name}</span>
                        <span className="pay-summary-value pay-summary-value--gold">
                          +{formatAddonEur(addon.priceEur)}
                        </span>
                      </div>
                    ) : null
                  )}
                  <div className="pay-summary-row">
                    <span className="pay-summary-label">Total</span>
                    <span className="pay-summary-value pay-summary-value--gold">
                      {getTotalAmount()} {selectedCrypto}
                    </span>
                  </div>
                </div>
              </div>

              <div className="cm-field">
                <label className="cm-field-label" htmlFor="telegram-username">
                  Telegram <span className="cm-field-required">*</span>
                </label>
                <input
                  id="telegram-username"
                  type="text"
                  value={telegramUsername}
                  onChange={(e) => setTelegramUsername(e.target.value)}
                  placeholder="@your_username"
                  className="cm-input"
                  readOnly={lockedTelegram}
                  disabled={lockedTelegram}
                />
                <p className="cm-field-hint">
                  {lockedTelegram
                    ? 'Username linked to your license — access will be delivered to this account'
                    : 'Sign-in details sent via Telegram'}
                </p>
              </div>

              <div>
                <p className="cm-field-label mb-2">Optional add-ons</p>
                <div className="space-y-2">
                  {PLAN_ADDON_OPTIONS.map((addon) => (
                    <div
                      key={addon.key}
                      className={`mm-toggle-option${selectedAddons[addon.key] ? ' mm-toggle-option--active' : ''}`}
                      onClick={() => toggleAddon(addon.key)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') toggleAddon(addon.key);
                      }}
                    >
                      <div>
                        <p className="cracker-option-name text-sm">{addon.name} add-on</p>
                        <p className="cracker-option-desc">
                          {addon.description}{' '}
                          <span className="text-[#e53935]">(+{formatAddonEur(addon.priceEur)}/mo)</span>
                        </p>
                      </div>
                      <div
                        className={`cracker-toggle${selectedAddons[addon.key] ? ' cracker-toggle--on' : ''}`}
                      >
                        <span className="cracker-toggle-knob" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="cm-field-label mb-2">Cryptocurrency</p>
                <div className="pay-crypto-grid">
                  {(['BTC', 'ETH', 'SOL'] as const).map((crypto) => (
                    <button
                      key={crypto}
                      type="button"
                      onClick={() => setSelectedCrypto(crypto)}
                      className={`pay-crypto-option${selectedCrypto === crypto ? ' pay-crypto-option--active' : ''}`}
                    >
                      <div className="pay-crypto-symbol">{cryptoSymbols[crypto]}</div>
                      <div className="pay-crypto-name">{crypto}</div>
                      <div className="pay-crypto-amount">
                        {plan.cryptoPrice[crypto]} {crypto}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <AnimatePresence>
                {error && (
                  <motion.div
                    className="auth-alert"
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                  >
                    <span aria-hidden="true">⚠</span>
                    <span>{error}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {paymentData && (paymentData as PaymentData).status === 'pending' && (
                <button
                  type="button"
                  onClick={async () => {
                    const pd = paymentData as PaymentData;
                    if (!pd.paymentId) return;
                    const res = await fetch(
                      `https://glitchcloud.app/api/payment/simulate-success/${pd.paymentId}`,
                      { method: 'POST' }
                    );
                    const data = await res.json();
                    if (data.success) {
                      setPaymentData({
                        paymentId: pd.paymentId,
                        paymentAddress: pd.paymentAddress,
                        paymentAmount: pd.paymentAmount,
                        paymentCurrency: pd.paymentCurrency,
                        expiresAt: pd.expiresAt,
                        status: 'completed',
                      });
                      setTimeout(() => onSuccess(), 1500);
                    } else {
                      alert('Simulation error: ' + (data.message || ''));
                    }
                  }}
                  className="cracker-btn cracker-btn--ghost w-full"
                >
                  Simulate successful payment (test)
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <div className="pay-waiting-icon">…</div>
                <h3 className="cracker-heading text-base mb-2">Awaiting payment</h3>
                <p className="text-xs text-[#737373] leading-relaxed">
                  Send exactly{' '}
                  <span className="text-[#e53935] font-mono font-bold">
                    {paymentData.paymentAmount} {paymentData.paymentCurrency}
                  </span>{' '}
                  to the address below
                </p>
              </div>

              <div className="pay-timer">
                <p className="pay-timer-value">Time remaining : {formatTime(timeLeft)}</p>
                {timeLeft <= 300 && (
                  <p className="text-[0.625rem] text-[#fca5a5] mt-1">Payment will expire soon</p>
                )}
              </div>

              <div className="cm-field">
                <label className="cm-field-label">Payment address</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={paymentData.paymentAddress}
                    readOnly
                    className="cm-input flex-1 text-[0.625rem]"
                  />
                  <button
                    type="button"
                    onClick={() => copyToClipboard(paymentData.paymentAddress)}
                    className="cracker-btn cracker-btn--ghost flex-shrink-0"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="pay-instructions">
                <p className="cracker-section-title mb-2">Instructions</p>
                <ul className="list-none space-y-1">
                  <li>Send exactly {paymentData.paymentAmount} {paymentData.paymentCurrency}</li>
                  <li>Automatic confirmation on receipt</li>
                  <li>Details sent via Telegram</li>
                  <li>Status updated every 10 seconds</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="cracker-modal-footer">
          {!paymentData ? (
            <>
              <button type="button" onClick={onClose} className="cracker-btn cracker-btn--ghost">
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePayment}
                disabled={isLoading || !telegramUsername.trim()}
                className="cracker-btn cracker-btn--gold"
              >
                {isLoading ? 'Creating…' : `Pay ${getTotalAmount()} ${selectedCrypto}`}
              </button>
            </>
          ) : (
            <button type="button" onClick={onClose} className="cracker-btn cracker-btn--ghost w-full">
              Close (payment continues in background)
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentModal;
